import { determineType, generateId, isLDEntry, toContainer, toLDValue } from "./utils";
const isIndexProp = (p) => typeof p === "string" && /^\d+$/.test(p);
// ---------- RGA helpers ----------
function cmpIds(a, b) {
    if (a.meta.updatedAt !== b.meta.updatedAt)
        return a.meta.updatedAt - b.meta.updatedAt;
    if (a.meta.replicaId !== b.meta.replicaId)
        return a.meta.replicaId < b.meta.replicaId ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
function linearizeRGA(arr) {
    var _a;
    const afterToKids = new Map();
    for (const id in arr.entries) {
        const e = arr.entries[id]; // <-- add type
        const after = ((_a = e.meta.after) !== null && _a !== void 0 ? _a : null);
        if (!afterToKids.has(after))
            afterToKids.set(after, []);
        afterToKids.get(after).push(e);
    }
    for (const [, list] of afterToKids.entries())
        list.sort(cmpIds);
    const out = [];
    const seen = new Set();
    const walk = (parent) => {
        const kids = afterToKids.get(parent) || [];
        for (const e of kids) {
            if (!seen.has(e.id)) {
                seen.add(e.id);
                if (!e.meta.tombstone)
                    out.push(e.id);
                walk(e.id);
            }
        }
    };
    walk(null);
    return out;
}
// ---------- CRDT ----------
export class CRDT {
    constructor(initial, merge_callback) {
        this.listeners = new Map();
        this.rootProxy = null;
        this.pending = [];
        this.clientReplicaId = "client";
        this.lastFlushAt = 0;
        this.minFlushMs = 50;
        this.trailingTimer = null;
        this.snapshot = initial;
        this.merge_callback = merge_callback;
    }
    state() {
        if (!this.rootProxy)
            this.rootProxy = this.makeProxy([]);
        return this.rootProxy;
    }
    async flush() {
        if (this.pending.length === 0)
            return;
        const ops = this.compactOps(this.pending);
        this.pending = [];
        this.merge(ops); // optimistic apply
        this.merge_callback(ops); // send outward
    }
    scheduleFlush() {
        const now = performance.now();
        const dueIn = (this.lastFlushAt + this.minFlushMs) - now;
        if (this.trailingTimer)
            return;
        if (dueIn <= 0) {
            this.lastFlushAt = now;
            this.flush();
        }
        else {
            this.trailingTimer = setTimeout(() => {
                this.trailingTimer = null;
                this.lastFlushAt = performance.now();
                this.flush();
            }, Math.ceil(dueIn));
        }
    }
    // ---- Op compaction (unchanged high level) ----
    compactOps(batch) {
        const parentAddsSeen = new Set();
        const parentAdds = [];
        const arrayOps = [];
        const leafMap = new Map();
        for (const op of batch) {
            if (op.op === "array-push" || op.op === "array-unshift" || op.op === "array-remove") {
                arrayOps.push(op);
                continue;
            }
            if (op.op === "add" && op.entry && op.entry.type !== "string"
                && op.entry.type !== "number" && op.entry.type !== "boolean") {
                const k = op.path.join(".");
                if (!parentAddsSeen.has(k)) {
                    parentAddsSeen.add(k);
                    parentAdds.push(op);
                }
                continue;
            }
            const key = op.path.join(".");
            if (op.op === "remove") {
                leafMap.set(key, { op: "remove" });
                continue;
            }
            if (op.op === "add" || op.op === "replace")
                leafMap.set(key, { op: "replace", entry: op.entry });
        }
        parentAdds.sort((a, b) => a.path.length - b.path.length);
        const leaves = [];
        for (const [k, acc] of leafMap) {
            const path = k.split(".");
            if (acc.op === "remove")
                leaves.push({ op: "remove", path, timestamp: Date.now(), replicaId: this.clientReplicaId });
            else
                leaves.push({ op: "replace", path, entry: acc.entry, timestamp: Date.now(), replicaId: this.clientReplicaId });
        }
        return parentAdds.concat(leaves, arrayOps);
    }
    // ---------- RGA visibility helpers (snapshot + pending overlay) ----------
    pendingOpsFor(parentPath) {
        const key = parentPath.join(".");
        return this.pending.filter(op => (op.op === "array-push" || op.op === "array-unshift" || op.op === "array-remove") &&
            op.path.join(".") === key);
    }
    ensureArrayContainer(path) {
        const cont = this.getContainerAt(path);
        if (cont && "entries" in cont)
            return cont;
        const parent = this.navigateToParent(path, true);
        const key = path[path.length - 1];
        const newArr = { entries: {}, head: null, meta: { updatedAt: Date.now(), version: 1, replicaId: this.clientReplicaId } };
        const entry = {
            id: generateId(),
            key,
            type: "array",
            value: newArr,
            meta: { updatedAt: Date.now(), version: 1, replicaId: this.clientReplicaId }
        };
        if ("index" in parent) {
            parent.index[key] = entry;
        }
        else {
            // arrays can hold objects as items, not named keys; so this branch shouldn't happen for array container creation
            throw new Error("Cannot create named array inside an array item");
        }
        return newArr;
    }
    // Auto-create missing nested maps for a path
    ensureMapParents(root, path, ts, replicaId) {
        let node = root;
        for (let i = 0; i < path.length - 1; i++) {
            const seg = path[i];
            // MAP container
            if ("index" in node) {
                if (!node.index[seg]) {
                    node.index[seg] = {
                        id: generateId(),
                        key: seg,
                        type: "object",
                        value: toLDValue({}),
                        meta: { updatedAt: ts, version: 1, replicaId }
                    };
                }
                node = node.index[seg].value;
                continue;
            }
            // ARRAY container (RGA) → element id
            const arr = node;
            if (!arr.entries[seg]) {
                arr.entries[seg] = {
                    id: seg,
                    key: seg,
                    type: "object",
                    value: toLDValue({}),
                    meta: { updatedAt: ts, version: 1, replicaId, after: null }
                };
            }
            node = arr.entries[seg].value;
        }
    }
    baseIdsFor(path) {
        const arr = this.getContainerAt(path);
        if (!arr)
            return [];
        return linearizeRGA(arr);
    }
    visibleIdsFor(path) {
        var _a;
        const ids = this.baseIdsFor(path).slice();
        const pend = this.pendingOpsFor(path);
        for (const op of pend) {
            if (op.op === "array-push") {
                const after = (_a = op.ref) !== null && _a !== void 0 ? _a : null;
                const pos = after ? (ids.indexOf(after) + 1) : ids.length;
                ids.splice(pos >= 0 ? pos : ids.length, 0, op.entry.id);
            }
            else if (op.op === "array-unshift") {
                ids.splice(0, 0, op.entry.id);
            }
            else if (op.op === "array-remove") {
                const i = ids.indexOf(op.ref);
                if (i >= 0)
                    ids.splice(i, 1);
            }
        }
        return ids;
    }
    getArrayIdAt(path, idx) {
        const ids = this.visibleIdsFor(path);
        const n = ids.length;
        const i = idx < 0 ? n + idx : idx;
        return (i >= 0 && i < n) ? ids[i] : undefined;
    }
    // ---------- Node navigation ----------
    navigate(path) {
        let node = this.snapshot;
        for (let i = 0; i < path.length; i++) {
            const seg = path[i];
            if (i === 0 && seg === "index")
                continue;
            if (typeof node === "object" && "index" in node) {
                const e = node.index[seg];
                if (!e)
                    throw new Error(`Path not found: ${path.join(".")}`);
                node = e.value;
            }
            else if (typeof node === "object" && "entries" in node) {
                const e = node.entries[seg];
                if (!e)
                    throw new Error(`Path not found: ${path.join(".")}`);
                node = e.value;
            }
            else {
                throw new Error(`Cannot navigate into primitive at "${seg}"`);
            }
        }
        return node;
    }
    readJSONAt(path) {
        try {
            let node = this.snapshot;
            for (let i = 0; i < path.length; i++) {
                const seg = path[i];
                if (typeof node === "object" && node && "index" in node) {
                    const e = node.index[seg];
                    if (!e)
                        return undefined;
                    node = e.value;
                }
                else if (typeof node === "object" && "entries" in node) {
                    const e = node.entries[seg];
                    if (!e)
                        return undefined;
                    node = e.value;
                }
                else {
                    return undefined;
                }
            }
            return this.toJSON(node);
        }
        catch (_a) {
            return undefined;
        }
    }
    navigateToParent(path, forceCreate) {
        let node = this.snapshot;
        for (let i = 0; i < path.length - 1; i++) {
            const seg = path[i];
            if (i === 0 && seg === "index")
                continue;
            if ("index" in node) {
                if (!node.index[seg]) {
                    if (!forceCreate)
                        throw new Error(`Invalid path: ${path.join(".")}`);
                    node.index[seg] = this.forceCreatePath(node, seg, path[i + 1]);
                }
                node = toContainer(node.index[seg].value);
            }
            else {
                const arr = node;
                let e = arr.entries[seg]; // <-- type it
                if (!e) {
                    if (!forceCreate)
                        throw new Error(`Invalid path: ${path.join(".")}`);
                    const id = seg;
                    e = {
                        id,
                        key: id,
                        type: "object",
                        value: toLDValue({}),
                        meta: { updatedAt: Date.now(), version: 1, replicaId: this.clientReplicaId, after: null },
                    };
                    arr.entries[id] = e;
                }
                node = toContainer(e.value);
            }
        }
        return node;
    }
    forceCreatePath(node, seg, nextSeg) {
        const isArray = /^\d+$/.test(nextSeg) || nextSeg.startsWith("[");
        const ldVal = isArray ? toLDValue([]) : toLDValue({});
        const entry = {
            id: generateId(),
            key: seg,
            type: isArray ? "array" : "object",
            value: ldVal,
            meta: { updatedAt: Date.now(), version: 1, replicaId: this.clientReplicaId },
        };
        if ("entries" in node) {
            // arrays don't have named slots; this path only used for maps
            throw new Error("Cannot create named path inside array");
        }
        else {
            node.index[seg] = entry;
        }
        return entry;
    }
    getContainerAt(path) {
        let node = this.snapshot;
        for (let i = 0; i < path.length; i++) {
            const seg = path[i];
            if (node && typeof node === "object" && "index" in node) {
                const e = node.index[seg];
                if (!e)
                    return undefined;
                node = e.value;
            }
            else if (node && typeof node === "object" && "entries" in node) {
                const e = node.entries[seg];
                if (!e)
                    return undefined;
                node = e.value;
            }
            else {
                return undefined;
            }
        }
        if (node && typeof node === "object") {
            if ("entries" in node || "index" in node)
                return node;
        }
        return undefined;
    }
    // ---------- JSON view ----------
    toJSON(val) {
        if (typeof val === "string" || typeof val === "number" || typeof val === "boolean")
            return val;
        if (typeof val === "object" && "index" in val) {
            const out = {};
            for (const [k, e] of Object.entries(val.index)) {
                out[k] = this.toJSON(e.value);
            }
            return out;
        }
        if (typeof val === "object" && "entries" in val) {
            const arr = val;
            const ids = linearizeRGA(arr);
            return ids.map(id => this.toJSON(arr.entries[id].value)); // <-- !
        }
        return null;
    }
    getState() { return this.snapshot; }
    setReplicaId(id) { this.clientReplicaId = id; }
    getReplicaId() { return this.clientReplicaId; }
    // ---------- Proxy ----------
    makeProxy(parentPath) {
        const self = this;
        const handler = {
            get(_t, prop) {
                if (prop === "__path")
                    return parentPath;
                if (prop === "__flush")
                    return () => self.flush();
                if (prop === "length") {
                    return self.visibleIdsFor(parentPath).length;
                }
                if (prop === "push") {
                    return (...items) => {
                        self.ensureArrayContainer(parentPath);
                        // find current tail id
                        const cur = self.visibleIdsFor(parentPath);
                        let prev = cur.length ? cur[cur.length - 1] : null;
                        for (const it of items) {
                            const id = generateId();
                            const entry = {
                                id, key: id,
                                type: determineType(it),
                                value: toLDValue(it, self.clientReplicaId),
                                meta: { updatedAt: Date.now(), version: 1, replicaId: self.clientReplicaId, after: prev },
                            };
                            self.pending.push({
                                op: "array-push",
                                path: parentPath.slice(),
                                ref: prev !== null && prev !== void 0 ? prev : undefined,
                                entry,
                                timestamp: entry.meta.updatedAt,
                                replicaId: self.clientReplicaId,
                            });
                            prev = id;
                        }
                        self.scheduleFlush();
                        return (cur.length + items.length);
                    };
                }
                if (prop === "unshift") {
                    return (...items) => {
                        self.ensureArrayContainer(parentPath);
                        // User order preserved via timestamps (items created sequentially)
                        for (const it of items) {
                            const id = generateId();
                            const entry = {
                                id, key: id,
                                type: determineType(it),
                                value: toLDValue(it, self.clientReplicaId),
                                meta: { updatedAt: Date.now(), version: 1, replicaId: self.clientReplicaId, after: null },
                            };
                            self.pending.push({
                                op: "array-unshift",
                                path: parentPath.slice(),
                                entry,
                                timestamp: entry.meta.updatedAt,
                                replicaId: self.clientReplicaId,
                            });
                        }
                        self.scheduleFlush();
                        return self.visibleIdsFor(parentPath).length;
                    };
                }
                if (prop === "pop") {
                    return () => {
                        const ids = self.visibleIdsFor(parentPath);
                        if (ids.length === 0)
                            return undefined;
                        const idToRemove = ids[ids.length - 1];
                        // best-effort return from snapshot
                        const arr = self.getContainerAt(parentPath);
                        let ret = undefined;
                        if (arr && arr.entries[idToRemove]) {
                            ret = self.toJSON(arr.entries[idToRemove].value);
                        }
                        self.pending.push({ op: "array-remove", path: parentPath.slice(), ref: idToRemove, timestamp: Date.now(), replicaId: self.clientReplicaId });
                        self.scheduleFlush();
                        return ret;
                    };
                }
                if (prop === "removeAt") {
                    return (index) => {
                        const id = self.getArrayIdAt(parentPath, index);
                        if (!id)
                            return undefined;
                        const arr = self.getContainerAt(parentPath);
                        let ret = undefined;
                        if (arr && arr.entries[id])
                            ret = self.toJSON(arr.entries[id].value);
                        self.pending.push({ op: "array-remove", path: parentPath.slice(), ref: id, timestamp: Date.now(), replicaId: self.clientReplicaId });
                        self.scheduleFlush();
                        return ret;
                    };
                }
                if (prop === "splice") {
                    return (start, deleteCount, ...items) => {
                        const ids = self.visibleIdsFor(parentPath);
                        const len = ids.length;
                        const s = Math.min(Math.max(0, start | 0), len);
                        const dc = deleteCount == null ? (len - s) : Math.max(0, Math.min(deleteCount | 0, len - s));
                        // deletes
                        for (let i = 0; i < dc; i++) {
                            const id = ids[s + i];
                            if (id)
                                self.pending.push({ op: "array-remove", path: parentPath.slice(), ref: id, timestamp: Date.now(), replicaId: self.clientReplicaId });
                        }
                        // inserts: insert after predecessor (s-1), or at head if s==0
                        let prev = s > 0 ? ids[s - 1] : null;
                        for (const it of items) {
                            const id = generateId();
                            const entry = {
                                id, key: id,
                                type: determineType(it),
                                value: toLDValue(it, self.clientReplicaId),
                                meta: { updatedAt: Date.now(), version: 1, replicaId: self.clientReplicaId, after: prev },
                            };
                            self.pending.push({
                                op: prev ? "array-push" : "array-unshift",
                                path: parentPath.slice(),
                                ref: prev !== null && prev !== void 0 ? prev : undefined,
                                entry,
                                timestamp: entry.meta.updatedAt,
                                replicaId: self.clientReplicaId,
                            });
                            prev = id;
                        }
                        self.scheduleFlush();
                        return ids.slice(s, s + dc).map(id => ({ id }));
                    };
                }
                // numeric index → element proxy
                if (isIndexProp(prop)) {
                    const idx = Number(prop);
                    const id = self.getArrayIdAt(parentPath, idx);
                    if (!id)
                        return undefined;
                    return self.makeProxy(parentPath.concat(id));
                }
                // ---- Object semantics ----
                const key = String(prop);
                const full = parentPath.concat(key);
                const val = self.readJSONAt(full);
                if (val !== undefined && typeof val === "object")
                    return self.makeProxy(full);
                if (val === undefined)
                    return self.makeProxy(full); // lazy creation
                return val; // primitive
            },
            set(_t, prop, value) {
                // array index assignment
                if (isIndexProp(prop)) {
                    const idx = Number(prop);
                    const id = self.getArrayIdAt(parentPath, idx);
                    self.ensureArrayContainer(parentPath);
                    if (id) {
                        const full = parentPath.concat(id);
                        const entry = {
                            id, key: id,
                            type: determineType(value),
                            value: toLDValue(value, self.clientReplicaId),
                            meta: { updatedAt: Date.now(), version: 1, replicaId: self.clientReplicaId },
                        };
                        self.pending.push({ op: "replace", path: full, entry, timestamp: entry.meta.updatedAt, replicaId: self.clientReplicaId });
                    }
                    else if (idx === self.visibleIdsFor(parentPath).length) {
                        // treat as append
                        const pushFn = handler.get(_t, "push");
                        pushFn(value);
                    }
                    else {
                        throw new Error(`Cannot create sparse index ${idx}. Use splice/insert.`);
                    }
                    self.scheduleFlush();
                    return true;
                }
                // object property assignment
                const key = String(prop);
                const full = parentPath.concat(key);
                // ensure parents (maps only)
                const parents = self.ensureParents(full);
                const entry = {
                    id: generateId(),
                    key,
                    type: determineType(value),
                    value: toLDValue(value, self.clientReplicaId),
                    meta: { updatedAt: Date.now(), version: 1, replicaId: self.clientReplicaId },
                };
                self.pending.push(...parents, { op: "replace", path: full, entry, timestamp: entry.meta.updatedAt, replicaId: self.clientReplicaId });
                self.scheduleFlush();
                return true;
            },
            deleteProperty(_t, prop) {
                if (isIndexProp(prop)) {
                    const id = self.getArrayIdAt(parentPath, Number(prop));
                    if (!id)
                        return true;
                    self.pending.push({ op: "array-remove", path: parentPath.slice(), ref: id, timestamp: Date.now(), replicaId: self.clientReplicaId });
                    self.scheduleFlush();
                    return true;
                }
                const key = String(prop);
                const full = parentPath.concat(key);
                if (self.readJSONAt(full) === undefined)
                    return true;
                self.pending.push({ op: "remove", path: full, timestamp: Date.now(), replicaId: self.clientReplicaId });
                self.scheduleFlush();
                return true;
            },
            ownKeys() {
                const ids = self.visibleIdsFor(parentPath);
                return Array.from({ length: ids.length }, (_, i) => String(i));
            },
            getOwnPropertyDescriptor() { return { configurable: true, enumerable: true }; }
        };
        return new Proxy({}, handler);
    }
    // Create parent maps along a path (for objects only)
    ensureParents(full) {
        const ops = [];
        for (let i = 0; i < full.length - 1; i++) {
            const sub = full.slice(0, i + 1);
            const exists = this.readJSONAt(sub) !== undefined;
            if (exists)
                continue;
            const seg = full[i];
            const parentPath = full.slice(0, i); // what contains `seg`?
            const parentCont = this.getContainerAt(parentPath);
            // If parent is an array container, never auto-create named children here;
            // array elements are created by array ops or by the merge fallback above.
            if (parentCont && "entries" in parentCont) {
                // skip creating; merge() will upsert the element if needed
                continue;
            }
            // Otherwise, create a map at this level
            const entry = {
                id: generateId(),
                key: seg,
                type: "object",
                value: toLDValue({}),
                meta: { updatedAt: Date.now(), version: 1, replicaId: this.clientReplicaId },
            };
            ops.push({ op: "add", path: sub, entry, timestamp: Date.now(), replicaId: this.clientReplicaId });
        }
        return ops;
    }
    // ---------- Merge ----------
    merge(ops) {
        var _a;
        for (const op of ops) {
            // Array inserts
            if (op.op === "array-push" || op.op === "array-unshift") {
                const arr = this.ensureArrayContainer(op.path);
                const e = op.entry;
                // ensure deterministic after
                if (op.op === "array-push")
                    e.meta.after = ((_a = op.ref) !== null && _a !== void 0 ? _a : null);
                if (op.op === "array-unshift")
                    e.meta.after = null;
                // take timestamp from op for ordering
                e.meta.updatedAt = op.timestamp;
                e.meta.replicaId = op.replicaId;
                // upsert by id
                arr.entries[e.id] = e;
                continue;
            }
            if (op.op === "array-remove") {
                const arr = this.ensureArrayContainer(op.path);
                const target = arr.entries[op.ref];
                if (target) {
                    // LWW tombstone
                    if (!target.meta.tombstone || (target.meta.updatedAt <= op.timestamp)) {
                        target.meta.tombstone = true;
                        target.meta.updatedAt = op.timestamp;
                        target.meta.replicaId = op.replicaId;
                    }
                }
                continue;
            }
            if (op.op === "remove") {
                const parent = this.navigateToParent(op.path, false);
                const key = op.path[op.path.length - 1];
                if ("index" in parent) {
                    delete parent.index[key];
                }
                else {
                    const arr = parent;
                    delete arr.entries[key]; // key is an id in RGA arrays
                }
                continue;
            }
            if (op.op === "add" || op.op === "replace") {
                let parent;
                try {
                    this.ensureMapParents(this.snapshot, op.path, op.timestamp, op.replicaId);
                    parent = this.navigateToParent(op.path, op.op === "add");
                }
                catch (err) {
                    // Fallback: if this is a replace inside an array element, upsert the element
                    const p = op.path;
                    // need at least: [..., arrayKey, elementId, field]
                    if (op.op === "replace" && p.length >= 3) {
                        const arrayPath = p.slice(0, -2);
                        const elemId = p[p.length - 2];
                        const maybeArr = this.getContainerAt(arrayPath);
                        if (maybeArr && "entries" in maybeArr) {
                            const arr = maybeArr;
                            if (!arr.entries[elemId]) {
                                // create a placeholder object element so we can set its fields
                                arr.entries[elemId] = {
                                    id: elemId,
                                    key: elemId,
                                    type: "object",
                                    value: toLDValue({}),
                                    meta: {
                                        updatedAt: op.timestamp,
                                        version: 1,
                                        replicaId: op.replicaId,
                                        after: null, // unknown predecessor (safe for field edits)
                                    },
                                };
                            }
                            // now the parent path exists; re-run the regular flow
                            parent = this.navigateToParent(op.path, false);
                        }
                        else {
                            throw err;
                        }
                    }
                    else {
                        throw err;
                    }
                }
                const key = op.path[op.path.length - 1];
                const entry = op.entry;
                if ("index" in parent) {
                    parent.index[key] = entry;
                }
                else {
                    const arr = parent;
                    if (!isLDEntry(entry))
                        throw new Error("Expected LDEntry for array element replace");
                    arr.entries[key] = entry; // key is id
                }
                continue;
            }
            // fallback no-op
        }
        const affected = new Set(ops.map(o => o.path.join(".")));
        const affectedArr = Array.from(affected);
        this.listeners.forEach((cbs, subPath) => {
            if (affectedArr.some(p => p.startsWith(subPath))) {
                this.query(subPath).execute().then(json => cbs.forEach(cb => cb(json)));
            }
        });
    }
    // ---------- Public query API ----------
    query(path) {
        const segments = !path || path === "" || path === "index" ? [] : path.split(".");
        const execute = async () => {
            try {
                return this.toJSON(this.navigate(segments));
            }
            catch (_a) {
                return null;
            }
        };
        const listen = async (cb) => {
            const key = path || "";
            if (!this.listeners.has(key))
                this.listeners.set(key, new Set());
            this.listeners.get(key).add(cb);
            cb(await execute());
            return () => this.listeners.get(key).delete(cb);
        };
        return { execute, listen };
    }
}
